import axios from "axios";

import { config } from "../config";

export const VGS_CONFIG_UNAVAILABLE = "VGS_CONFIG_UNAVAILABLE";

export interface WidgetVgsConfig {
  vaultId: string;
  environment: "sandbox" | "live";
  proxyHost: string;
  collect?: { routeId?: string; version?: string };
}

export class VgsConfigUnavailableError extends Error {
  readonly code = VGS_CONFIG_UNAVAILABLE;

  constructor() {
    super(VGS_CONFIG_UNAVAILABLE);
    this.name = "VgsConfigUnavailableError";
  }
}

export const isVgsConfigUnavailable = (error: unknown): boolean =>
  error instanceof VgsConfigUnavailableError ||
  (typeof error === "object" &&
    error !== null &&
    (error as { code?: string }).code === VGS_CONFIG_UNAVAILABLE);

const isConfig = (value: unknown): value is WidgetVgsConfig => {
  if (!value || typeof value !== "object") return false;
  const vgs = value as WidgetVgsConfig;
  return (
    typeof vgs.vaultId === "string" &&
    vgs.vaultId.length > 0 &&
    typeof vgs.proxyHost === "string" &&
    vgs.proxyHost.length > 0 &&
    (vgs.environment === "sandbox" || vgs.environment === "live") &&
    !vgs.proxyHost.includes("undefined")
  );
};

let seeded: WidgetVgsConfig | null = null;
let pending: Promise<WidgetVgsConfig> | null = null;

/** Prefer claim/session config.vgs when the session is stored. */
export const rememberVgsConfig = (value: unknown): void => {
  if (isConfig(value)) seeded = value;
};

export const loadVgsConfig = (): Promise<WidgetVgsConfig> => {
  if (seeded) return Promise.resolve(seeded);
  if (!pending) {
    pending = fetchVgsConfig().catch((error) => {
      pending = null;
      throw error;
    });
  }
  return pending;
};

const ROUTE_ID = /^[a-z0-9-]+$/i;

export const vgsPostUrl = async (): Promise<string> => {
  const vgs = await loadVgsConfig();
  const routeId = vgs.collect?.routeId?.trim();
  const host =
    routeId && ROUTE_ID.test(routeId)
      ? `${vgs.vaultId}-${routeId}.${vgs.environment}.verygoodproxy.com`
      : vgs.proxyHost;
  return `https://${host}/post`;
};

const fetchVgsConfig = async (): Promise<WidgetVgsConfig> => {
  const base = config.API_BASE_URL.replace(/\/$/, "");
  const { data } = await axios.get(`${base}/widget/config/tokenization`, {
    headers: { "Cache-Control": "no-cache" },
  });
  const vgs = data?.data?.vgs ?? data?.vgs;
  if (!isConfig(vgs)) throw new VgsConfigUnavailableError();
  seeded = vgs;
  return vgs;
};
